import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useUndoRedoStore } from "../thread-v5/undoRedoStore";
import { reduceLoomAuthoringCommand, type LoomAuthoringCommand } from "./loomAuthoringModel";
import {
  acceptLoomAuthoringCommand,
  currentLoomAuthoringDocument,
  currentLoomExtractionDirty,
  deriveCurrentLoomDependentAncestry,
  redoLoomAuthoringHistory,
  undoLoomAuthoringHistory,
  type LoomAuthoringHistory,
  type LoomCommandAcceptedOperation,
  type LoomCommandAcceptance,
} from "./loomAuthoringHistory";
import {
  LoomHistoryStorageConflictError,
  LoomHistoryStorageCoordinationError,
  loadLoomAuthoringHistoryStorage,
  loomHistoryUserUndoFloor,
  loomHistoryStorageV2Key,
  persistLoomAuthoringHistoryStorage,
  type LoomAuthoringHistoryLoadResult,
  type LoomHistoryStorageStatus,
} from "./loomAuthoringHistoryStorage";
import type { LoomControlWorkspaceContext, LoomControlWorkspaceIntent } from "./loomControlWorkspace";
import {
  acceptLoomControlWorkspaceIntent,
  currentLoomControlWorkspace,
  redoLoomControlWorkspaceHistory,
  undoLoomControlWorkspaceHistory,
  type LoomControlWorkspaceHistory,
} from "./loomControlWorkspaceHistory";

interface LoomUndoSnapshot { cursor: number; stateId: string }

export type LoomNativeAnalyticJacobianParameterId =
  | "sweep_deg"
  | "taper"
  | "chord_root"
  | "half_span"
  | "twist_tip_deg"
  | "dihedral_deg"
  | "te_thickness";

export interface LoomAuthoringPreviewRequest {
  controlId: string;
  backend: "explicit-parametric-regeneration";
  analyticJacobianParameterId: LoomNativeAnalyticJacobianParameterId | null;
}

export interface LoomAuthoringPreview extends LoomAuthoringPreviewRequest {
  baseRevision: number;
  document: ReturnType<typeof currentLoomAuthoringDocument>;
}

interface PendingPreview {
  command: LoomAuthoringCommand;
  request: LoomAuthoringPreviewRequest;
}

interface StorageWriteContext {
  projectId: string;
  expectedV2Raw: string | null;
  desiredGeneration: number;
  queue: Promise<void>;
}

export type LoomPersistenceWriteState = "pending" | "persisted" | "blocked";

function loadHistory(projectId: string): LoomAuthoringHistoryLoadResult {
  if (typeof window === "undefined") return loadLoomAuthoringHistoryStorage(null, projectId);
  try { return loadLoomAuthoringHistoryStorage(window.localStorage, projectId); }
  catch { return loadLoomAuthoringHistoryStorage(null, projectId); }
}

function initialWriteState(loaded: LoomAuthoringHistoryLoadResult): LoomPersistenceWriteState {
  return loaded.persistence === "blocked" ? "blocked" : "pending";
}

function isUndoSnapshot(value: unknown): value is LoomUndoSnapshot {
  if (typeof value !== "object" || value === null) return false;
  const item = value as Record<string, unknown>;
  return Number.isInteger(item.cursor) && typeof item.stateId === "string";
}

function undoRedoSnapshots(
  history: LoomAuthoringHistory,
  status: LoomHistoryStorageStatus,
): { past: LoomUndoSnapshot[]; future: LoomUndoSnapshot[] } {
  const floor = loomHistoryUserUndoFloor(history, status);
  const accepted = new Map(history.operations
    .filter((operation): operation is LoomCommandAcceptedOperation => operation.kind === "command.accepted")
    .map((operation) => [operation.operationId, operation]));
  const snapshotAt = (cursor: number): LoomUndoSnapshot => {
    if (cursor === 0) return { cursor, stateId: history.initialHead.stateId };
    const operation = accepted.get(history.activeBranchOperationIds[cursor - 1]!);
    if (!operation) throw new Error(`active branch operation ${cursor - 1} is missing`);
    return { cursor, stateId: operation.result.stateId };
  };
  return {
    past: Array.from({ length: history.cursor - floor + 1 }, (_, index) => snapshotAt(floor + index)),
    future: Array.from(
      { length: history.activeBranchOperationIds.length - history.cursor },
      (_, index) => snapshotAt(history.cursor + index + 1),
    ),
  };
}

function hydrateUndoScope(scope: string, history: LoomAuthoringHistory, status: LoomHistoryStorageStatus): void {
  useUndoRedoStore.getState().hydrate(scope, undoRedoSnapshots(history, status));
}

function readOnlyReason(status: LoomHistoryStorageStatus): string | null {
  if (status.kind === "legacy-v1-retained") return "This unrecognized legacy Pattern is inspection-only until it is explicitly migrated or exported.";
  if (status.kind === "storage-blocked") return `This Pattern is inspection-only because browser persistence is blocked (${status.reason}).`;
  return null;
}

export function useLoomAuthoringHistory(projectId: string) {
  const [loaded, setLoaded] = useState(() => loadHistory(projectId));
  const [persistenceWriteState, setPersistenceWriteState] = useState<LoomPersistenceWriteState>(() => initialWriteState(loaded));
  const history = loaded.history;
  const workspaceHistory = loaded.workspaceHistory;
  const document = useMemo(() => currentLoomAuthoringDocument(history), [history]);
  const [preview, setPreview] = useState<LoomAuthoringPreview | null>(null);
  const historyRef = useRef(history);
  const documentRef = useRef(document);
  const workspaceHistoryRef = useRef(workspaceHistory);
  const previewFrameRef = useRef<number | null>(null);
  const pendingPreviewRef = useRef<PendingPreview | null>(null);
  const storageStatusRef = useRef(loaded.status);
  const storageWriteRef = useRef<StorageWriteContext>({ projectId, expectedV2Raw: loaded.persistedV2Raw, desiredGeneration: 0, queue: Promise.resolve() });
  historyRef.current = history;
  documentRef.current = document;
  workspaceHistoryRef.current = workspaceHistory;
  storageStatusRef.current = loaded.status;
  const undoFloor = loomHistoryUserUndoFloor(history, loaded.status);
  const scope = `loom-authoring:${projectId}`;
  const pendingRestore = useUndoRedoStore((state) => state.pendingRestore);

  useEffect(() => {
    if (previewFrameRef.current !== null) cancelAnimationFrame(previewFrameRef.current);
    previewFrameRef.current = null;
    pendingPreviewRef.current = null;
    setPreview(null);
    const next = loadHistory(projectId);
    storageWriteRef.current = { projectId, expectedV2Raw: next.persistedV2Raw, desiredGeneration: 0, queue: Promise.resolve() };
    historyRef.current = next.history;
    documentRef.current = currentLoomAuthoringDocument(next.history);
    workspaceHistoryRef.current = next.workspaceHistory;
    storageStatusRef.current = next.status;
    setPersistenceWriteState(initialWriteState(next));
    setLoaded(next);
  }, [projectId]);

  useEffect(() => () => {
    if (previewFrameRef.current !== null) cancelAnimationFrame(previewFrameRef.current);
  }, []);

  useEffect(() => {
    if (loaded.projectId !== projectId) return;
    if (loaded.persistence !== "v2") return;
    const context = storageWriteRef.current;
    const generation = context.desiredGeneration;
    setPersistenceWriteState("pending");
    context.queue = context.queue.then(async () => {
      context.expectedV2Raw = await persistLoomAuthoringHistoryStorage(
        window.localStorage, projectId, history, workspaceHistory, loaded.status, context.expectedV2Raw,
      );
      if (storageWriteRef.current === context && context.desiredGeneration === generation) {
        setPersistenceWriteState("persisted");
      }
    }).catch((error: unknown) => {
      if (storageWriteRef.current !== context) return;
      const status: LoomHistoryStorageStatus = {
        kind: "storage-blocked",
        reason: error instanceof LoomHistoryStorageConflictError
          ? "cross-tab-conflict"
          : error instanceof LoomHistoryStorageCoordinationError
            ? "coordination-unavailable"
            : "storage-unavailable",
        message: error instanceof Error ? error.message : "Browser storage is unavailable.",
      };
      setPersistenceWriteState("blocked");
      setLoaded((current) => ({ ...current, status, persistence: "blocked" }));
    });
  }, [history, loaded.persistence, loaded.projectId, loaded.status, projectId, workspaceHistory]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      const context = storageWriteRef.current;
      if (event.key !== loomHistoryStorageV2Key(projectId) || event.newValue === context.expectedV2Raw) return;
      setPersistenceWriteState("blocked");
      setLoaded((current) => ({
        ...current,
        status: { kind: "storage-blocked", reason: "cross-tab-conflict", message: "Another tab changed this Loom journal; reload before editing." },
        persistence: "blocked",
      }));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [projectId]);

  useEffect(() => {
    const store = useUndoRedoStore.getState();
    store.registerScope(scope);
    hydrateUndoScope(scope, historyRef.current, storageStatusRef.current);
    return () => store.unregisterScope(scope);
  }, [scope]);

  const cancelPreview = useCallback(() => {
    if (previewFrameRef.current !== null) cancelAnimationFrame(previewFrameRef.current);
    previewFrameRef.current = null;
    pendingPreviewRef.current = null;
    setPreview(null);
  }, []);

  const commit = (next: LoomAuthoringHistory) => {
    cancelPreview();
    storageWriteRef.current.desiredGeneration += 1;
    setPersistenceWriteState("pending");
    historyRef.current = next;
    documentRef.current = currentLoomAuthoringDocument(next);
    hydrateUndoScope(scope, next, storageStatusRef.current);
    setLoaded((current) => ({ ...current, history: next }));
  };

  const commitWorkspace = (next: LoomControlWorkspaceHistory) => {
    storageWriteRef.current.desiredGeneration += 1;
    setPersistenceWriteState("pending");
    workspaceHistoryRef.current = next;
    setLoaded((current) => ({ ...current, workspaceHistory: next }));
  };

  const previewCommand = useCallback((
    command: LoomAuthoringCommand,
    request: LoomAuthoringPreviewRequest,
  ): string | null => {
    const blocked = readOnlyReason(storageStatusRef.current);
    if (blocked) return blocked;
    if (command.expectedRevision !== historyRef.current.activeHead.revision) {
      return `revision conflict: expected ${command.expectedRevision}, current ${historyRef.current.activeHead.revision}`;
    }
    pendingPreviewRef.current = { command, request };
    if (previewFrameRef.current !== null) return null;
    previewFrameRef.current = requestAnimationFrame(() => {
      previewFrameRef.current = null;
      const pending = pendingPreviewRef.current;
      pendingPreviewRef.current = null;
      if (!pending) return;
      try {
        const baseRevision = historyRef.current.activeHead.revision;
        if (pending.command.expectedRevision !== baseRevision) return;
        setPreview({
          ...pending.request,
          baseRevision,
          document: reduceLoomAuthoringCommand(documentRef.current, pending.command).document,
        });
      } catch {
        // The committed document remains authoritative when a transient input becomes invalid.
        setPreview(null);
      }
    });
    return null;
  }, []);

  useEffect(() => {
    if (!pendingRestore || pendingRestore.scope !== scope) return;
    const snapshot = useUndoRedoStore.getState().consumeRestore(scope);
    if (!isUndoSnapshot(snapshot)) return;
    try {
      let next = historyRef.current;
      while (next.cursor > snapshot.cursor) next = undoLoomAuthoringHistory(next);
      while (next.cursor < snapshot.cursor) next = redoLoomAuthoringHistory(next);
      if (next.activeHead.stateId === snapshot.stateId) commit(next);
    } catch {
      // A stale generic restore cannot override the validated Loom journal.
    }
  }, [pendingRestore?.nonce, scope]);

  const acceptCommand = (
    command: LoomAuthoringCommand,
    acceptance: LoomCommandAcceptance = { origin: "api.command" },
  ): string | null => {
    const blocked = readOnlyReason(storageStatusRef.current);
    if (blocked) return blocked;
    try {
      const next = acceptLoomAuthoringCommand(historyRef.current, command, acceptance);
      commit(next);
      return null;
    } catch (error) {
      return error instanceof Error ? error.message : "Unable to apply the Loom command";
    }
  };

  const acceptWorkspaceIntent = (
    intent: LoomControlWorkspaceIntent,
    context: LoomControlWorkspaceContext,
  ): string | null => {
    const blocked = readOnlyReason(storageStatusRef.current);
    if (blocked) return blocked;
    try {
      const next = acceptLoomControlWorkspaceIntent(workspaceHistoryRef.current, intent, context, {
        origin: "assistant.ollama",
        documentRevision: historyRef.current.activeHead.revision,
        documentStateId: historyRef.current.activeHead.stateId,
      });
      commitWorkspace(next);
      return null;
    } catch (error) {
      return error instanceof Error ? error.message : "Unable to update the Loom control workspace";
    }
  };

  const moveWorkspace = (direction: "undo" | "redo") => {
    if (readOnlyReason(storageStatusRef.current)) return;
    const current = workspaceHistoryRef.current;
    if (direction === "undo" ? current.cursor === 0 : current.cursor === current.activeBranchOperationIds.length) return;
    try {
      commitWorkspace(direction === "undo"
        ? undoLoomControlWorkspaceHistory(current)
        : redoLoomControlWorkspaceHistory(current));
    } catch {
      // The visible workspace controls are disabled at the branch boundary.
    }
  };

  const move = (direction: "undo" | "redo") => {
    if (readOnlyReason(storageStatusRef.current)) return;
    if (direction === "undo" && historyRef.current.cursor <= loomHistoryUserUndoFloor(historyRef.current, storageStatusRef.current)) return;
    const store = useUndoRedoStore.getState();
    const available = direction === "undo" ? store.canUndo(scope) : store.canRedo(scope);
    if (available) {
      if (direction === "undo") store.undo(scope);
      else store.redo(scope);
      return;
    }
    try {
      commit(direction === "undo"
        ? undoLoomAuthoringHistory(historyRef.current)
        : redoLoomAuthoringHistory(historyRef.current));
    } catch {
      // The visible controls are disabled at the boundary; ignore a stale click.
    }
  };

  const workspace = useMemo(() => currentLoomControlWorkspace(workspaceHistory), [workspaceHistory]);
  const ancestry = useMemo(() => deriveCurrentLoomDependentAncestry(history), [history]);
  const dependentIds = new Set(ancestry.commandOperationIds);
  const lastOperation = [...history.operations].reverse().find((operation): operation is LoomCommandAcceptedOperation => (
    operation.kind === "command.accepted" && operation.origin !== "system.migration" && dependentIds.has(operation.operationId)
  ));
  const lastCommand = lastOperation?.command ?? null;
  return {
    acceptCommand,
    acceptWorkspaceIntent,
    document,
    extractionDirty: currentLoomExtractionDirty(history),
    authoringReadOnlyReason: readOnlyReason(loaded.status),
    history,
    lastCommand,
    preview,
    previewCommand,
    persistenceWriteState: loaded.projectId === projectId ? persistenceWriteState : "pending",
    cancelPreview,
    storageStatus: loaded.status,
    undoFloor,
    redo: () => move("redo"),
    undo: () => move("undo"),
    workspace,
    workspaceHistory,
    workspaceRedo: () => moveWorkspace("redo"),
    workspaceUndo: () => moveWorkspace("undo"),
  };
}
